import { uniqueId } from 'lodash/fp';
import { CSSProperties, useMemo } from 'react';
import toast, { useToasterStore } from 'react-hot-toast/headless';
import { DefaultTheme, useTheme } from 'styled-components';
import { TextKey } from '@mews-framework/localization';
import { Alerts, AlertVariant, APPBAR_DESKTOP_HEIGHT, getSpacingToken, ZIndex } from '@optimus-web/core';
import { ALERT_DEFAULT_CLOSE_TIMEOUT } from '../../constants';
import { AlertProps, AlertsItemProps } from './types';

interface AlertsProviderProps {
    zIndex: CSSProperties['zIndex'];
}

/* Created because 'react-hot-toast' Toast currently doesn't provide any way to pass custom properties */
const FakeCustomMessage = (_props: AlertsItemProps) => null;

const triggerToast = (props: AlertProps, variant: AlertVariant) => {
    const id = props.id || uniqueId('alert');
    const closeTimeout = props.closeTimeout || ALERT_DEFAULT_CLOSE_TIMEOUT;
    const onClose = () => {
        toast.remove(id);
        props.onClose?.();
    };

    return toast.custom(
        <FakeCustomMessage
            {...props}
            closeTimeout={closeTimeout}
            description={props.description}
            id={id}
            onClose={onClose}
            title={props.title}
            variant={variant}
        />,
        { id }
    );
};

const getDefaultAlertsOffset = (theme: DefaultTheme) => APPBAR_DESKTOP_HEIGHT + getSpacingToken('700')({ theme });

export const AlertsProvider: React.FC<React.PropsWithChildren<AlertsProviderProps>> = ({
    children,
    zIndex = ZIndex.Highest,
}) => {
    const theme = useTheme();
    const desktopOffset = getDefaultAlertsOffset(theme);
    const { toasts } = useToasterStore();

    const alerts = useMemo(
        () => toasts.map(({ message }) => (message as JSX.Element).props as AlertsItemProps),
        [toasts]
    );

    return (
        <>
            {children}
            {alerts.length > 0 ? (
                <div style={{ position: 'fixed', zIndex }}>
                    <Alerts alerts={alerts} closeButtonLabel={TextKey.Close} desktopOffset={desktopOffset} />
                </div>
            ) : null}
        </>
    );
};

export const closeAlert = (id: string) => toast.remove(id);
export const closeAlerts = () => toast.remove();
export const showErrorAlert = (props: AlertProps) => triggerToast(props, AlertVariant.Error);
export const showInfoAlert = (props: AlertProps) => triggerToast(props, AlertVariant.Info);
export const showSuccessAlert = (props: AlertProps) => triggerToast(props, AlertVariant.Success);
export const showWarningAlert = (props: AlertProps) => triggerToast(props, AlertVariant.Warning);
